import { ChangeEvent, useState, useContext } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  Divider,
  useTheme,
  alpha,
  IconButton,
  Stack,
  Tooltip,
  DialogActions
} from "@mui/material";
import { useChatOptions } from "../hooks/useChatOptions";
import { FormFactor } from "../types/enums/FormFactor";
import { useFormFactor } from "../hooks/useFormFactor";
import { MigrationContext } from "./MigrationAssistantModal";
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';

export type GlobalSettingsProps = {
  open: boolean;
  currentDefaultSystemPrompt: string;
  currentUseRagByDefault: boolean;
  onSave: (newDefaultSystemPrompt: string, newUseRagByDefault: boolean) => void;
  onClose: () => void;
};

export default function GlobalSettings({
  open,
  currentDefaultSystemPrompt,
  currentUseRagByDefault,
  onSave,
  onClose,
}: GlobalSettingsProps) {
  const [defaultSystemPrompt, setDefaultSystemPrompt] = useState<string>(
    currentDefaultSystemPrompt,
  );
  const [useRagByDefault, setUseRagByDefault] = useState<boolean>(
    currentUseRagByDefault,
  );
  const { chatOptions } = useChatOptions();
  const formFactor = useFormFactor();
  const showRag = chatOptions.show_rag;
  const { triggerMigration } = useContext(MigrationContext);
  const theme = useTheme();

  const handleClose = () => {
    setDefaultSystemPrompt(currentDefaultSystemPrompt);
    onClose();
  };

  const handleSave = () => {
    onSave(defaultSystemPrompt, useRagByDefault);
    onClose();
  };

  const handleDefaultSystemPromptChange = (
    event: ChangeEvent<{ value: unknown }>,
  ) => {
    setDefaultSystemPrompt(event.target.value as string);
  };

  const handleUseRagByDefaultChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setUseRagByDefault(event.target.checked);
  };

  const handleResetPrompt = () => {
    setDefaultSystemPrompt(chatOptions.default_system_prompt);
  };

  const handleMigrationClick = () => {
    console.log("Migration button clicked");
    
    // Close the settings dialog first
    onClose();
    
    // Set a global flag to indicate migration should start
    // This bypasses any React state issues
    window.showMigrationDialog = true;
    
    // Force a direct dialog using a global approach
    if (typeof window.openMigration === 'function') {
      console.log("Calling global openMigration function");
      window.openMigration();
    } else {
      // Fallback to regular approach with delay
      console.log("Using context approach with delay");
      setTimeout(() => {
        if (triggerMigration) {
          triggerMigration();
        }
      }, 500);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth={true}
      fullScreen={formFactor < FormFactor.Tablet}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: theme.palette.primary.main, 
        color: theme.palette.primary.contrastText,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 1
      }}>
        <Typography variant="h6" component="div">
          Global Settings
        </Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={handleClose} 
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined }}>
        <Typography variant="body2" sx={{ 
          mb: 3, 
          p: 1.5, 
          backgroundColor: alpha(theme.palette.info.main, 0.1), 
          borderRadius: 1,
          borderLeft: `4px solid ${theme.palette.info.main}`
        }}>
          <strong>Note:</strong> These changes will only be applied to newly created conversations.
        </Typography>
        
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3, 
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : alpha(theme.palette.background.paper, 0.7),
            borderRadius: 2
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 500 }}>
            Default System Prompt
          </Typography>
          
          <TextField
            sx={{ 
              width: "100%", 
              mb: 1,
              '& .MuiInputBase-root': {
                bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined
              }
            }}
            multiline={true}
            minRows={4}
            maxRows={10}
            value={defaultSystemPrompt}
            placeholder="Enter the default system prompt for new conversations"
            onChange={handleDefaultSystemPromptChange}
            variant="outlined"
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="Reset to default system prompt">
              <Button 
                onClick={handleResetPrompt} 
                startIcon={<RestartAltIcon />}
                size="small"
                variant="outlined"
                color="primary"
              >
                Reset
              </Button>
            </Tooltip>
          </Box>
        </Paper>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
          Data Management
        </Typography>
        
        <Stack spacing={2}>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<DeleteIcon />} 
            onClick={() => {}}
            sx={{ 
              justifyContent: 'flex-start',
              borderRadius: 1.5
            }}
          >
            Delete all Conversations
          </Button>
          
          <Button 
            variant="outlined" 
            color="error"
            startIcon={<MoveToInboxIcon />}
            onClick={handleMigrationClick}
            sx={{ 
              justifyContent: 'flex-start',
              borderRadius: 1.5
            }}
          >
            Migration Assistant
          </Button>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        py: 2,
        backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : alpha(theme.palette.background.paper, 0.5),
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ 
            minWidth: '100px',
            borderRadius: 1.5
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          color="primary"
          sx={{ 
            minWidth: '100px',
            borderRadius: 1.5,
            ml: 2
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
